import { z } from 'zod';
import { classSchema } from './class.js';
import { itemSchema } from './item.js';
import { backgroundSchema, featSchema, subclassSchema } from './origins.js';
import { speciesSchema } from './species.js';
import { spellSchema } from './spell.js';
/**
 * Описание формы записи — выведенное из схемы, а не написанное рядом
 * с ней (D53, шаг 3).
 *
 * Редактор контента спрашивает у мастера ровно те поля, по которым
 * запись валидируется. Список этих полей нельзя держать вторым
 * экземпляром в коде экрана: он разошёлся бы со схемой на первой же
 * правке, и мастер получал бы отказ при сохранении за поле, которого
 * форма не показала. Поэтому форма считается из самой схемы —
 * `z.toJSONSchema` даёт её строение, а обход ниже переводит строение
 * в поля.
 *
 * Что сюда не попадает и почему:
 *
 * - `id`, `type`, `source` — их проставляет сервер: ключ собирается
 *   из владельца, вид выбран на шаге 1, источник у пользовательской
 *   записи всегда «свой» (`customSourceSchema`);
 * - `translation` — перевод чужого текста (D32); свой текст мастер
 *   пишет сразу на своём языке, переводить его не у кого;
 * - `effects` — у них свой редактор (шаг 4), и в форме они помечены
 *   отдельным видом поля, а не парой «операция строкой».
 *
 * Поле, строение которого обход не узнал, помечается `unsupported`
 * и получает значение по умолчанию из схемы (`null` у необязательного,
 * пустой список у массива). Врать про такое поле нельзя: интерфейс
 * говорит, что оно заполняется только копированием записи.
 */
/** Виды записей, которые мастер заводит сам (D53, шаг 2). */
export const CUSTOM_TYPES = [
    'item',
    'spell',
    'subclass',
    'species',
    'background',
    'feat',
    'class',
];
export const customTypeSchema = z.enum(CUSTOM_TYPES);
const SCHEMAS = {
    item: itemSchema,
    spell: spellSchema,
    subclass: subclassSchema,
    species: speciesSchema,
    background: backgroundSchema,
    feat: featSchema,
    class: classSchema,
};
/** Поля, которые проставляет сервер: только у самой записи. */
const OMITTED_ROOT = new Set(['id', 'type', 'source']);
/**
 * Перевод не спрашивается нигде, включая вложенные особенности:
 * это перевод чужого текста (D32), а свой мастер пишет сразу
 * на своём языке.
 */
const OMITTED_ANYWHERE = new Set(['translation']);
/** Разбор `anyOf` вида «значение либо null»: очень частая форма. */
function unwrapNullable(node) {
    const branches = node.anyOf ?? node.oneOf;
    if (branches === undefined)
        return { inner: node, nullable: false };
    const nulls = branches.filter((branch) => branch.type === 'null');
    const rest = branches.filter((branch) => branch.type !== 'null');
    if (nulls.length === 0 || rest.length !== 1 || rest[0] === undefined) {
        return { inner: node, nullable: false };
    }
    return { inner: rest[0], nullable: true };
}
/**
 * Ветвь размеченного объединения: у неё есть поле-различитель.
 *
 * Различителем бывает и константа (`category: 'armor'`), и короткое
 * перечисление (`category: 'gear' | 'tool'` — у снаряжения
 * и инструментов механика одна и та же). Обе формы дают ветви;
 * перечисление — по ветви на значение.
 */
function discriminatorOf(branch) {
    for (const [key, value] of Object.entries(branch.properties ?? {})) {
        if (typeof value.const === 'string')
            return key;
        if (Array.isArray(value.enum) && value.enum.every((v) => typeof v === 'string')) {
            return key;
        }
    }
    return null;
}
/** Значения различителя у ветви: одно у константы, несколько у enum. */
function discriminatorValues(branch, key) {
    const node = branch.properties?.[key];
    if (node === undefined)
        return [];
    if (typeof node.const === 'string')
        return [node.const];
    if (Array.isArray(node.enum)) {
        return node.enum.filter((value) => typeof value === 'string');
    }
    return [];
}
/**
 * Значение по умолчанию для поля, которого форма не спрашивает.
 *
 * `null` у необязательного, пустой список у массива, ничего у всего
 * остального — и тогда вид записи просто не заводится через редактор.
 */
function fallbackFor(node, nullable) {
    if (nullable)
        return null;
    if (node.type === 'array')
        return [];
    return undefined;
}
function isEffectsField(key) {
    return key === 'effects';
}
/**
 * Счётчики (D56) спрашиваются своим видом поля, как эффекты: обход
 * схемы сложил бы их в группу с размеченным объединением внутри,
 * и получилась бы форма, в которой человек выбирает «kind: ref»
 * вместо «столько же, сколько бонус мастерства».
 */
function isResourcesField(key) {
    return key === 'resources';
}
/**
 * Выборы при получении (D58) — своим видом поля, как эффекты
 * и счётчики. Обход схемы сложил бы их в группу с размеченным
 * объединением внутри (`from.kind: list | catalogue`), и мастер
 * выбирал бы «kind: catalogue» вместо «любой навык».
 */
function isChoicesField(key) {
    return key === 'choices';
}
function describe(key, path, raw, required) {
    const { inner, nullable } = unwrapNullable(raw);
    const base = { path, key, required, nullable };
    if (isEffectsField(key))
        return { ...base, kind: 'effects' };
    if (isResourcesField(key))
        return { ...base, kind: 'resources' };
    if (isChoicesField(key))
        return { ...base, kind: 'choices' };
    if (Array.isArray(inner.enum) && inner.enum.length > 0) {
        return {
            ...base,
            kind: 'enum',
            options: inner.enum.filter((value) => typeof value === 'string'),
        };
    }
    if (typeof inner.const === 'string') {
        return { ...base, kind: 'enum', options: [inner.const] };
    }
    if (inner.type === 'boolean')
        return { ...base, kind: 'boolean' };
    if (inner.type === 'number' || inner.type === 'integer') {
        const min = inner.minimum ?? inner.exclusiveMinimum;
        const max = inner.maximum ?? inner.exclusiveMaximum;
        return {
            ...base,
            kind: 'number',
            integer: inner.type === 'integer',
            // Границы Number.MAX_SAFE_INTEGER схема ставит сама; на экране
            // они значат «без предела» и только мешают.
            ...(min !== undefined && Math.abs(min) < 1e6 ? { min } : {}),
            ...(max !== undefined && Math.abs(max) < 1e6 ? { max } : {}),
        };
    }
    if (inner.type === 'string') {
        // Ссылка на другую запись узнаётся по образцу из `contentIdSchema`:
        // такое поле выбирают из справочника, а не набирают руками —
        // опечатка в ключе означает эффект, который молча не применится.
        if (inner.pattern?.includes('srd:') === true) {
            return { ...base, kind: 'contentRef' };
        }
        // Длинный текст отличается от короткого не схемой, а назначением:
        // описание записи человек пишет абзацем, название — строкой.
        return { ...base, kind: key === 'text' ? 'longText' : 'text' };
    }
    const branches = inner.oneOf ?? inner.anyOf;
    if (branches !== undefined && branches.length > 0) {
        const discriminator = discriminatorOf(branches[0] ?? {});
        if (discriminator !== null) {
            const variants = [];
            for (const branch of branches) {
                const fields = fieldsOf(branch, path).filter((field) => field.key !== discriminator);
                for (const value of discriminatorValues(branch, discriminator)) {
                    variants.push({ value, fields });
                }
            }
            if (variants.length > 0) {
                return {
                    ...base,
                    kind: 'variant',
                    options: variants.map((v) => v.value),
                    variants,
                };
            }
        }
        return { ...base, kind: 'unsupported', fallback: fallbackFor(inner, nullable) };
    }
    if (inner.type === 'array') {
        const item = inner.items;
        if (item === undefined) {
            return { ...base, kind: 'unsupported', fallback: [] };
        }
        if (item.type === 'string' || item.type === 'number' || item.type === 'integer') {
            return {
                ...base,
                kind: 'list',
                fields: [describe('item', `${path}.item`, item, true)],
            };
        }
        if (item.type === 'object') {
            return { ...base, kind: 'list', fields: fieldsOf(item, `${path}.item`) };
        }
        return { ...base, kind: 'unsupported', fallback: [] };
    }
    if (inner.type === 'object') {
        return { ...base, kind: 'group', fields: fieldsOf(inner, path) };
    }
    return { ...base, kind: 'unsupported', fallback: fallbackFor(inner, nullable) };
}
function fieldsOf(node, prefix) {
    const required = new Set(node.required ?? []);
    const fields = [];
    for (const [key, value] of Object.entries(node.properties ?? {})) {
        if (OMITTED_ANYWHERE.has(key))
            continue;
        if (prefix === '' && OMITTED_ROOT.has(key))
            continue;
        const path = prefix === '' ? key : `${prefix}.${key}`;
        fields.push(describe(key, path, value, required.has(key)));
    }
    return fields;
}
const cache = new Map();
/**
 * Поля формы для вида записи.
 *
 * Схема разбирается один раз на вид: `toJSONSchema` не бесплатен,
 * а строение схемы за время работы процесса не меняется.
 */
export function contentFormFields(type) {
    const known = cache.get(type);
    if (known !== undefined)
        return known;
    const json = z.toJSONSchema(SCHEMAS[type], {
        io: 'input',
        unrepresentable: 'any',
    });
    const fields = fieldsOf(json, '');
    cache.set(type, fields);
    return fields;
}
/** Все виды разом: редактор спрашивает форму один раз на сеанс. */
export function contentForms() {
    const out = {};
    for (const type of CUSTOM_TYPES)
        out[type] = contentFormFields(type);
    return out;
}
//# sourceMappingURL=form.js.map